import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@assetwatch/ui";
import type { AssetSort, AssetSortField } from "../../../lib/assetTreeSearch";
import type { AssetId } from "../../../lib/workIds";
import { type AssetViewRow, directChildCount } from "./assetModel";

type AssetsTableProps = {
  allRows: AssetViewRow[];
  rows: AssetViewRow[];
  emptyMessage: string;
  onNavigate: (id: number) => void;
  onSelectRecord: (assetId: AssetId) => void;
  sort?: AssetSort;
  onSortChange: (field: AssetSortField) => void;
};

export function AssetsTable({
  allRows,
  rows,
  emptyMessage,
  onNavigate,
  onSelectRecord,
  sort,
  onSortChange,
}: AssetsTableProps) {
  function sortDirectionFor(field: AssetSortField) {
    if (sort?.field !== field) return false;
    return sort.dir === "desc" ? "descending" : "ascending";
  }

  function handleRowClick(assetId: AssetId) {
    return () => onSelectRecord(assetId);
  }

  return (
    <div className="min-h-0 flex-1 overflow-auto [&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10">
      <Table aria-label="Assets" size="sm">
        <TableHeader>
          <TableRow section="header">
            <TableHead
              onSort={() => onSortChange("name")}
              scope="col"
              sortable
              sortDirection={sortDirectionFor("name")}
            >
              Asset
            </TableHead>
            <TableHead
              onSort={() => onSortChange("type")}
              scope="col"
              sortable
              sortDirection={sortDirectionFor("type")}
            >
              Type
            </TableHead>
            <TableHead scope="col">Location</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const childCount = directChildCount(allRows, row.id);
            const assetId = row.assetId;
            return (
              <TableRow
                interactive={Boolean(assetId)}
                key={row.id}
                onClick={assetId ? handleRowClick(assetId) : undefined}
              >
                <TableCell variant="starting">
                  <TableCell.Starting
                    icon={
                      childCount > 0 ? (
                        <IconButton
                          aria-label={`Open ${row.name}`}
                          icon="chevron-right"
                          onClick={(event) => {
                            event.stopPropagation();
                            onNavigate(row.id);
                          }}
                          size="sm"
                          variant="ghost"
                        />
                      ) : (
                        <span className="inline-block size-(--spacing-32)" />
                      )
                    }
                    primary={
                      assetId ? (
                        <button
                          className="block rounded-(--radius-sm) border-0 bg-transparent p-0 text-left text-inherit outline-none focus-visible:ring-2 focus-visible:ring-button-focus-ring"
                          onClick={(event) => {
                            event.stopPropagation();
                            onSelectRecord(assetId);
                          }}
                          type="button"
                        >
                          {row.name}
                        </button>
                      ) : (
                        row.name
                      )
                    }
                    secondary={row.code}
                  />
                </TableCell>
                <TableCell>{row.typeName ?? "—"}</TableCell>
                <TableCell>{row.locationCode ?? "—"}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {rows.length === 0 ? (
        <div className="p-(--spacing-24) text-center">
          <Text tone="subtle">{emptyMessage}</Text>
        </div>
      ) : null}
    </div>
  );
}
